import { LLMHistory } from "@dto/llm.dto";
import { Avatar, Button, Modal, ModalProps, Popover, TooltipProps } from "antd";
import { createContext, FC, useEffect, useMemo, useState } from "react";
import { LuBotMessageSquare } from "react-icons/lu";
import { Label } from "src/data/Label";
import { Log } from "src/service/log.service";
import { updateUser } from "../userProfile/UserProfile";
import './aiChat.less';
import { AIChatConversation } from "./AIChatConversation";
import { AIChatHistory } from "./AIChatHistory";
import { chatAborter, getChatHistory } from "./chatService";
import AIChatPopover from "./components/AIChatPopover";

class ChatModel {
  conversation: LLMHistory;
  setConversation: React.Dispatch<React.SetStateAction<LLMHistory>>;
  history: LLMHistory[];
  setHistory: React.Dispatch<React.SetStateAction<LLMHistory[]>>;
  chatOpen: boolean;
  historyOpen: boolean;
  setHistoryOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ChatContext = createContext(new ChatModel());

export const AIChat: FC<{ open: boolean; }> = (props) => {
  const [conversation, setConversation] = useState<LLMHistory>();
  const [history, setHistory] = useState<LLMHistory[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const ctx = useMemo<ChatModel>(() => ({ conversation, setConversation, history, setHistory, chatOpen: props.open, historyOpen, setHistoryOpen }),
    [conversation, setConversation, history, setHistory, props.open, historyOpen, setHistoryOpen]);

  useEffect(() => {
    chatAborter.current = new AbortController();
    void getChatHistory().then(response => {
      setHistory(response.data.data);
    });
    return () => {
      chatAborter.current?.abort();
    };
  }, []);

  function showDisclaimer() {
    setDisclaimerOpen(true);
  }

  function hideDisclaimer() {
    setDisclaimerOpen(false);
  }

  return <div className="xm-chat">
    <ChatContext.Provider value={ctx}>
      <AIChatConversation />
      <AIChatHistory />
    </ChatContext.Provider>
    <div className="xm-chat-disclaimer">{Label.XORBOT} can make mistakes. Check important <Button type="link" onClick={showDisclaimer}>info.</Button></div>
    <ChatDisclaimer open={disclaimerOpen} onOk={hideDisclaimer} onCancel={hideDisclaimer} />
  </div>;
};

const ChatDisclaimer: FC<ModalProps> = props => {
  return <Modal {...props} title="Disclaimer" cancelButtonProps={{ style: { display: 'none' } }} width="50vw">
    <div>
      This chatbot is powered by a third-party large language model (LLM), which generates responses based on patterns in data and does not possess human judgment, consciousness, or intent. While we strive to provide accurate and helpful information, the responses generated may be incomplete, outdated, or incorrect.
      <br /><br />
      <div className="text-bold">By using this chatbot, you acknowledge and agree to the following:</div>
      <ul>
        <li>The content provided is for informational purposes only and should not be considered professional advice (legal, medical, financial, etc.).</li>
        <li>We do not guarantee the accuracy, reliability, or suitability of any responses provided by the chatbot.</li>
        <li>Any reliance on information provided by this chatbot is at your own risk.</li>
        <li>We are not responsible for any damages, losses, or consequences resulting from the use of this chatbot or the information it provides.</li>
        <li>The chatbot may produce outputs that do not reflect the views or opinions of our organization or the developers of the third-party LLM.</li>
      </ul>
      If you require specific professional advice, please consult a qualified expert.
    </div>
  </Modal>;
};

export const ChatIcon: FC<{ promo?: boolean; }> = props => {
  const onInfoOpenChange: TooltipProps['onOpenChange'] = (visible) => {
    if (visible) {
      updateUser({ configuration: { aiShown: true } }).then(() => {
        Log.debug('User AI shown updated.');
      }, reason => Log.error('Failed to update user info!', reason));
    }
  };

  return props.promo ? <Popover
    title={Label.XORBOT}
    onOpenChange={onInfoOpenChange}
    trigger="click" placement="topRight"
    content={<div>
      You can now communicate with XorMon using our AI chat - Xbot.<br />
      Set up AI chat in Settings -&gt; Application -&gt; AI Chat.<br />
      Please refer to the <a target="_blank" href="https://xormon.com/AI.php">documentation</a>
    </div>}>
    <Avatar className="xm-chat-icon" size={44} icon={<LuBotMessageSquare />} alt="AI assistant" />
  </Popover>
    : <AIChatPopover />;
};